<template>
  <v-dialog
    v-model="show"
    :max-width="isSmaller ? '720px' : '850px'"
    :width="isSmaller ? '720px' : '850px'"
    :fullscreen="isMobile"
  >
    <div :class="{ 'customer-dialog': true, 'smaller': isSmaller }">
      <v-card tile :loading="isLoading ? 'secondary' : false" class="customer-dialog-card">
        <v-toolbar
          style="height: auto;"
          elevation="0"
          class="py-3"
          color="white"
        >
          <template v-if="isFetchingCustomer">
            <v-skeleton-loader
              type="avatar"
              style="width: 48px; height: 48px;"
            ></v-skeleton-loader>
            <div>
              <v-toolbar-title class="pl-5 mt-1 flex-shrink-1">
                <v-skeleton-loader
                  type="heading"
                  style="width: 170px; max-width: 170px;"
                ></v-skeleton-loader>
              </v-toolbar-title>
              <v-toolbar-title class="toolbar-subheader pl-5 mt-2 flex-shrink-1">
                <v-skeleton-loader
                  type="text"
                  style="width: 120px; max-width: 120px;"
                ></v-skeleton-loader>
              </v-toolbar-title>
            </div>
          </template>
          <template v-else>
            <customer-avatar :customer="customer" large></customer-avatar>
            <div class="dialog-title-container">
              <v-toolbar-title class="pl-5">{{ title }}</v-toolbar-title>
              <v-toolbar-title class="pl-5 toolbar-subheader">{{ subtitle }}</v-toolbar-title>
            </div>
          </template>
          <v-spacer></v-spacer>
          <v-tooltip v-if="!isOtaCustomer" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                :disabled="isFetchingCustomer"
                @click="openCustomerForm"
                class="mr-2"
              >
                <v-icon>mdi-pen</v-icon>
              </v-btn>
            </template>
            <div>
              Redigera kund
            </div>
          </v-tooltip>
          <v-btn icon v-if="isMobile" @click="show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-tabs
          v-model="tabIndex"
          color="secondary"
          show-arrows
          grow
        >
          <v-tabs-slider color="secondary"></v-tabs-slider>
          <v-tab href="#customer-information">
            <v-icon v-if="isMobile">mdi-account</v-icon>
            <span v-else>Kundinformation</span>
          </v-tab>
          <v-tab href="#customer-history">
            <v-icon v-if="isMobile">mdi-book-clock-outline</v-icon>
            <span v-else>Historik</span>
          </v-tab>
          <v-tab href="#bookings">
            <v-badge
              :value="bookingsCount !== ''"
              color="secondary"
              :content="bookingsCount"
              offset-x="-2"
              offset-y="8"
            >
              <v-icon v-if="isMobile">mdi-book-marker-outline</v-icon>
              <span v-else>Bokningar</span>
            </v-badge>
          </v-tab>
          <v-tab href="#invoices-receipts">
            <v-badge
              :value="receiptsAndInvoicesCount !== ''"
              color="secondary"
              :content="receiptsAndInvoicesCount"
              offset-x="-2"
              offset-y="8"
            >
              <v-icon v-if="isMobile">mdi-receipt-text</v-icon>
              <span v-else>Kvitton och fakturor</span>
            </v-badge>
          </v-tab>
        </v-tabs>
        <v-divider></v-divider>
        <v-slide-y-transition leave-absolute>
          <v-tabs
            v-show="tabIndex === 'invoices-receipts'"
            v-model="subTabIndex"
            centered
            slider-color="secondary"
          >
            <v-tab
              href="#receipts"
              class="px-8"
            >
              <v-badge
                :value="receiptCount !== ''"
                color="secondary"
                :content="receiptCount"
                offset-x="-2"
                offset-y="8"
              >
                Kvitton
              </v-badge>
            </v-tab>
            <v-tab
              href="#invoices"
              class="px-8"
            >
              <v-badge
                :value="invoiceCount !== ''"
                color="secondary"
                :content="invoiceCount"
                offset-x="-2"
                offset-y="8"
              >
                Fakturor
              </v-badge>
            </v-tab>
          </v-tabs>
        </v-slide-y-transition>
        <v-divider v-show="tabIndex === 'invoices-receipts'"></v-divider>
        <v-tabs-items v-model="tabIndex">
          <v-tab-item value="customer-information">
            <v-sheet class="overflow-y-auto pa-3 full-height">
              <v-row class="ma-0 full-height">
                <v-col class="py-0 full-height">
                  <customer-data-list
                    :is-loading="isFetchingCustomer"
                    :customer="customer"
                    :hidden-sections="['name']"
                  ></customer-data-list>
                  <!-- CKE-kort -->
                  <template v-if="scrEnabled && !isFetchingCustomer">
                    <v-subheader>Camping Key Europe</v-subheader>
                    <v-list-item v-if="isLinkingCke">
                      <v-list-item-icon>
                        <v-icon>mdi-credit-card-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-progress-circular
                            indeterminate
                            size="14"
                            width="2"
                            class="mr-2"
                          />
                          {{ isScanningCke ? 'Kopplar kort...' : 'Kopplar bort...' }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-else-if="customerCkeCardNumber">
                      <v-list-item-icon>
                        <v-icon>mdi-credit-card-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ customerCkeCardFormatted }}
                          <v-btn
                            x-small
                            text
                            color="error"
                            class="ml-2"
                            @click="unlinkCkeCard"
                          >
                            <v-icon
                              left
                              x-small
                            >mdi-link-off</v-icon>
                            Koppla bort
                          </v-btn>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-else>
                      <v-list-item-icon>
                        <v-icon>mdi-credit-card-plus-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title v-if="isScanningCke">
                          <v-chip
                            small
                            color="blue lighten-4"
                            text-color="blue darken-2"
                          >
                            <v-progress-circular
                              indeterminate
                              size="14"
                              width="2"
                              class="mr-1"
                            />
                            Väntar på skanning...
                          </v-chip>
                          <v-btn
                            x-small
                            text
                            class="ml-2"
                            @click="stopCkeScan"
                          >Avbryt</v-btn>
                        </v-list-item-title>
                        <v-list-item-title v-else>
                          <v-btn
                            x-small
                            text
                            color="primary"
                            @click="startCkeScan"
                          >
                            <v-icon
                              left
                              x-small
                            >mdi-credit-card-scan-outline</v-icon>
                            Skanna CKE-kort
                          </v-btn>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-col>
                <v-col class="pb-0 pt-6 full-height">
                  <v-alert
                    v-if="isOtaCustomer"
                    color="info"
                    text
                    dense
                    class="ma-0"
                  >
                    <div class="d-flex">
                      <v-icon color="info">mdi-information</v-icon>
                      <div class="ml-4">
                        Detta är en systemkund som representerar bokningar som ska betalas av {{ customer.ota_name }}.
                        Kunden ska endast användas för att debitera virtuella kort som ställts ut av en OTA.
                      </div>
                    </div>
                  </v-alert>
                  <customer-credit-timeline
                    v-else
                    :customer="customer"
                    :customer-credit-timeline="customerCreditTimeline"
                    :loading="isFetchingCustomerCreditTimeline"
                  ></customer-credit-timeline>
                </v-col>
              </v-row>
            </v-sheet>
          </v-tab-item>
          <v-tab-item value="customer-history">
            <v-sheet class="overflow-y-auto pa-3 full-height">
              <editing-record-timeline
                :editing-records="editingRecords"
                :loading="fetchingChunks.includes('editing-records')"
              ></editing-record-timeline>
            </v-sheet>
          </v-tab-item>
          <v-tab-item value="bookings">
            <v-sheet
              class="full-height overflow-y-auto px-3"
            >
              <bookings-list
                :bookings="bookings"
                :booking-groups="bookingGroups"
                :loading="fetchingChunks.includes('bookings')"
                @booking-clicked="showBooking"
                @close="close"
              ></bookings-list>
            </v-sheet>
          </v-tab-item>
          <v-tab-item value="invoices-receipts">
            <v-tabs-items v-model="subTabIndex">
              <v-tab-item value="receipts">
                <v-sheet class="full-height">
                  <receipt-list
                    class="full-height"
                    flat
                    :show-total-row="receipts.length > 1"
                    :loading="fetchingChunks.includes('receipts')"
                    :receipts="receipts"
                    :displayed-columns="['receipt_number', 'total', 'created_at']"
                  ></receipt-list>
                </v-sheet>
              </v-tab-item>
              <v-tab-item value="invoices">
                <v-sheet class="full-height">
                  <invoice-list
                    class="full-height"
                    flat
                    :show-total-row="invoices.length > 1"
                    :loading="fetchingChunks.includes('invoices')"
                    :invoices="invoices"
                    :displayed-columns="['invoice_number', 'total', 'remaining_amount', 'invoice_date', 'due_date', 'status']"
                  ></invoice-list>
                </v-sheet>
              </v-tab-item>
            </v-tabs-items>
          </v-tab-item>
        </v-tabs-items>
        <div class="d-flex flex-column overflow-hidden" style="position: absolute; height: 100%; width: 100%; top: 0; left: 0; pointer-events: none; z-index: 2;">
          <v-fade-transition>
            <div v-show="showBookingOverlay" @click="showBookingOverlay = false" style="height: 88px; min-height: 88px; opacity: 0.23; background-color: rgb(33, 33, 33); pointer-events: auto;"></div>
          </v-fade-transition>
          <v-scroll-y-reverse-transition>
            <booking-dialog-content
              v-show="showBookingOverlay"
              is-overlay
              :booking="bookingOverlay.booking"
              :charges="bookingOverlay.charges"
              :invoices="bookingOverlay.invoices"
              :receipts="bookingOverlay.receipts"
              :receipt-payments="bookingOverlay.receiptPayments"
              :invoice-payment-applications="bookingOverlay.invoicePaymentApplications"
              :editing-records="bookingOverlay.editingRecords"
              :emails="bookingOverlay.emails"
              :accommodation-charges-calendar-items="bookingOverlay.accommodationChargesCalendarItems"
              :accommodation-sums="bookingOverlay.accommodationSums"
              :is-loading="isFetchingCustomer || bookingOverlay.isLoading || bookingOverlay.bookingId === null"
              :is-open="showBookingOverlay"
              @close="close"
              class="flex-grow-1"
              style="pointer-events: auto;"
            ></booking-dialog-content>
          </v-scroll-y-reverse-transition>
        </div>
        <v-card-actions v-if="!isOtaCustomer" class="customer-dialog-footer full-width">
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            class="ma-2 px-4"
            large
            :disabled="isLoading"
            @click="startNewBooking"
          >
            Ny bokning
          </v-btn>
        </v-card-actions>
      </v-card>

      <div v-if="editedCustomer" class="d-flex flex-column overflow-hidden" style="position: absolute; height: 100%; width: 100%; top: 0; left: 0; pointer-events: none; z-index: 2;">
        <v-fade-transition>
          <div v-show="isShowCustomerForm" @click="closeCustomerForm" style="height: 76px; opacity: 0.23; background-color: rgb(33, 33, 33); pointer-events: auto;"></div>
        </v-fade-transition>
        <v-scroll-y-reverse-transition>
          <v-card
            v-show="isShowCustomerForm"
            class="flex-grow-1 overflow-y-auto"
            light
            style="pointer-events: auto; "
          >
            <v-toolbar class="flex-shrink-1 flex-grow-0" flat>
              <div class="d-flex">
                <v-toolbar-title class="headline">Redigera kund</v-toolbar-title>
              </div>
              <v-spacer></v-spacer>
              <v-btn icon @click="closeCustomerForm">
                <v-icon>
                  mdi-close
                </v-icon>
              </v-btn>
            </v-toolbar>
            <div class="flex-grow-1 px-7 pt-3">
              <customer-form
                v-model="editedCustomer"
                :is-open="isShowCustomerForm"
                savable
                @customer-saved="customerSaved"
              ></customer-form>
            </div>
          </v-card>
        </v-scroll-y-reverse-transition>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import SCRService from '@/services/SCRService.ts'
import CustomerDataList from '@/components/customer/DataList.vue'
import EditingRecordTimeline from '@/components/common/EditingRecordTimeline.vue'
import CustomerAvatar from '@/components/customer/Avatar.vue'
import CustomerCreditTimeline from '@/components/customer/CustomerCreditTimeline.vue'
import CustomerForm from '@/components/customer/Form.vue'
import BookingsList from '@/components/customer/BookingsList.vue'
import InvoiceList from '@/components/invoice/List.vue'
import ReceiptList from '@/components/receipt/List.vue'
import BookingDialogContent from '@/components/booking/DialogContent'

export default {
  name: 'CustomerDialog',
  components: {
    CustomerDataList,
    EditingRecordTimeline,
    CustomerAvatar,
    CustomerCreditTimeline,
    CustomerForm,
    BookingsList,
    InvoiceList,
    ReceiptList,
    BookingDialogContent
  },
  data: () => ({
    editedCustomer: null,
    isShowCustomerForm: false,
    // SCR/CKE
    isLinkingCke: false,
    isScanningCke: false
  }),
  computed: {
    ...mapState(
      {
        isSmaller: state => state.customer.dialog.isSmaller,
        customer: state => state.customer.dialog.customer,
        bookings: state => state.customer.dialog.bookings,
        bookingGroups: state => state.customer.dialog.bookingGroups,
        invoices: state => state.customer.dialog.invoices,
        receipts: state => state.customer.dialog.receipts,
        editingRecords: state => state.customer.dialog.editingRecords,
        customerCreditTimeline: state => state.customer.dialog.customerCreditTimeline,
        bookingOverlay: state => state.customer.dialog.bookingOverlay,
        isLoading: state => state.customer.dialog.isLoading,
        fetchingChunks: state => state.customer.dialog.fetchingChunks,
        openTab: state => state.customer.dialog.openTab,
        triggerInvoiceUpdated: state => state.invoice.triggerInvoiceUpdated,
        triggerBookingUpdated: state => state.booking.triggerBookingUpdated,
        triggerCustomerUpdated: state => state.customer.triggerCustomerUpdated,
        triggerCustomerCreditUpdated: state => state.invoice.triggerCustomerCreditUpdated
      }
    ),
    ...mapGetters(
      {
        scrEnabled: 'scr/isEnabled'
      }
    ),
    show: {
      get () {
        return this.$store.state.customer.dialog.show
      },
      set (val) {
        this.$store.commit('customer/SET_SHOW_DIALOG', val)
        if (!val) {
          this.showBookingOverlay = false
          this.closeCustomerForm()
        }
      }
    },
    showBookingOverlay: {
      get () {
        return this.bookingOverlay.show
      },
      set (val) {
        this.$store.commit('customer/SET_BOOKING_OVERLAY_SHOW', val)
      }
    },
    isFetchingCustomer: function () {
      return this.isLoading || this.fetchingChunks.includes('customer')
    },
    isFetchingCustomerCreditTimeline: function () {
      return this.isLoading || this.fetchingChunks.includes('customer-credit-timeline')
    },
    isOtaCustomer: function () {
      return this.customer && this.customer.is_ota_customer
    },
    tabIndex: {
      get () {
        const openTab = this.openTab
        if (openTab === 'invoices' || openTab === 'receipts' || openTab === 'payments') {
          return 'invoices-receipts'
        }
        return this.openTab
      },
      set (val) {
        if (val === 'invoices-receipts') {
          val = this.defaultSubTab
        }
        this.$store.commit('customer/SET_DIALOG_OPEN_TAB', val)
      }
    },
    subTabIndex: {
      get () {
        if (this.openTab !== 'receipts' && this.openTab !== 'invoices' && this.openTab !== 'payments') {
          return this.defaultSubTab
        }
        return this.openTab
      },
      set (val) {
        this.$store.commit('customer/SET_DIALOG_OPEN_TAB', val)
      }
    },
    defaultSubTab: function () {
      if (this.invoices.length === 0) {
        return 'receipts'
      }
      if (this.receipts.length === 0) {
        return 'invoices'
      }
      const mostRecentReceipt = this.receipts[0].created_at
      const mostRecentInvoice = this.invoices[0].created_at
      if (mostRecentInvoice.isAfter(mostRecentReceipt)) {
        return 'invoices'
      }
      return 'receipts'
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    receiptCount: function () {
      if (!this.receipts || this.receipts.length === 0) {
        return ''
      }
      if (this.receipts.length > 99) {
        return '99+'
      }
      return this.receipts.length
    },
    invoiceCount: function () {
      if (!this.invoices || this.invoices.length === 0) {
        return ''
      }
      if (this.invoices.length > 99) {
        return '99+'
      }
      return this.invoices.length
    },
    receiptsAndInvoicesCount: function () {
      if (!this.receipts || !this.invoices || (this.invoices.length + this.receipts.length) === 0) {
        return ''
      }
      const count = this.receipts.length + this.invoices.length
      if (count > 99) {
        return '99+'
      }
      return count
    },
    bookingsCount: function () {
      if (!this.bookings || this.bookings.length === 0) {
        return ''
      }
      const count = this.bookings.length
      if (count > 99) {
        return '99+'
      }
      return count
    },
    title: function () {
      if (!this.customer) {
        return ''
      }
      let title = this.customer.full_name
      if (this.customer.is_org) {
        title += ' (företagskund)'
      }
      return title
    },
    subtitle: function () {
      return this.customer && this.customer.customer_description
    },
    customerCkeCardNumber: function () {
      return this.customer?.cke_card_number || null
    },
    customerCkeCardFormatted: function () {
      const num = this.customerCkeCardNumber
      if (!num) { return '' }
      return num.replace(/(.{4})/g, '$1 ').trim()
    }
  },
  methods: {
    showBooking: function (booking) {
      if (this.isSmaller) {
        this.$store.commit('customer/SET_DIALOG_IS_SMALLER', false)
      }
      if (this.isMobile) {
        this.close()
        this.$store.dispatch('booking/openDialogById', { bookingId: booking.id })
      } else {
        this.$store.dispatch('customer/openBookingOverlay', { bookingId: booking.id })
      }
    },
    close: function () {
      this.show = false
    },
    openCustomerForm: function () {
      this.editedCustomer = Object.assign({}, this.customer)
      if (this.isSmaller) {
        this.$store.commit('customer/SET_DIALOG_IS_SMALLER', false)
      }
      this.isShowCustomerForm = true
    },
    closeCustomerForm: function () {
      this.isShowCustomerForm = false
    },
    customerSaved: function () {
      // ladda om uppdaterar customer objektet i dialogen
      this.$store.dispatch('customer/getCustomer', this.customer.id)
      this.closeCustomerForm()
    },
    startNewBooking: function () {
      return this.$store.dispatch('bookingForm/startNewBooking', {
        customer: this.customer
      })
    },
    unlinkCkeCard: function () {
      if (!this.customer?.id) { return }
      this.isLinkingCke = true
      SCRService.unlinkCustomer({ customer_id: this.customer.id })
        .then(({ data }) => {
          if (data.status === 'success') {
            this.customer.cke_card_number = null
          }
        })
        .finally(() => {
          this.isLinkingCke = false
        })
    },
    startCkeScan: function () {
      if (this.isScanningCke) { return }
      this.isScanningCke = true
      window.scannerVM?.requestInput('scr/handleScannerInput', null, false)
    },
    stopCkeScan: function () {
      if (!this.isScanningCke) { return }
      this.isScanningCke = false
      window.scannerVM?.abortInputRequest('scr/handleScannerInput')
      this.$store.commit('scr/SET_SEARCH_RESULT', null)
    },
    handleCkeScanResult: function (result) {
      if (!result || !this.isScanningCke || !this.customer?.id) { return }
      const cardNumber = result.scr?.card_number
      if (!cardNumber) {
        if (result.source !== 'none') {
          this.$store.dispatch('snackbars/createSnackbar', {
            color: 'error',
            text: 'Skanningen innehåller inget CKE-kortnummer'
          })
        }
        this.stopCkeScan()
        return
      }
      this.stopCkeScan()
      this.isLinkingCke = true
      SCRService.linkCustomer({ customer_id: this.customer.id, card_number: cardNumber })
        .then(({ data }) => {
          if (data.status === 'success') {
            this.customer.cke_card_number = cardNumber
          }
        })
        .finally(() => {
          this.isLinkingCke = false
        })
    }
  },
  watch: {
    triggerInvoiceUpdated: function () {
      // variabel som uppdateras efter fakturaändringar
      if (this.show && this.customer && this.customer.id) {
        this.$store.dispatch('customer/getCustomerInvoices', this.customer.id)
      }
    },
    triggerBookingUpdated: function () {
      if (this.show && this.customer && this.customer.id) {
        this.$store.dispatch('customer/getCustomerBookings', this.customer.id)
      }
    },
    triggerCustomerUpdated: function () {
      // Hämtar om editingRecords när customer har sparats
      if (this.show && this.customer && this.customer.id) {
        this.$store.dispatch('customer/getCustomerEditingRecords', this.customer.id)
      }
    },
    triggerCustomerCreditUpdated: function () {
      if (this.show && this.customer && this.customer.id) {
        this.$store.dispatch('customer/getCustomerCredit', this.customer.id)
      }
    },
    '$store.state.scr.searchResult': function (result) {
      this.handleCkeScanResult(result)
    },
    show: function (val) {
      if (!val) {
        this.stopCkeScan()
      }
    }
  }
}
</script>
